import React, { useEffect, useMemo, useRef } from "react";
import { colors2, makeArrayFromLength, SkeletonLoader, formatCamelCaseToSentenceCase } from "oolib";
import { extractAllBlocksFromTpl } from "@okf/ootils";
import { useGetQueryData } from "../../../../../../../utils/react-query-hooks/general";
import { useAggrMetricsAcrossDimensions } from "./queries/useAggrMetricsAcrossDimensions";
import { BarChartConfigForm } from "./ConfigForm";
import { WIDGET_ELEM_processRawConfigFn } from "./WIDGET_ELEM_processRawConfigFn";
import { WIDGET_ELEM_someReqPropsUnset } from "./WIDGET_ELEM_someReqPropsUnset";
import { extractErrorMsgs } from "@/utils/useGenErrorMsgBanners";
import {
  WidgetErrorState,
  WidgetLoadingState,
} from "../../WidgetRenderer/comps/altStatePlaceholders";
import { useUpdateLoadedWidgetsTracker } from "../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { useReportLandingContext } from "../../../ReportLandingContext";
import { BarChart } from "@/components/dataviz/BarChart";
import { DEFAULT_MAX_BARS, MIN_BARS } from "./constants";
import {
  filterByHiddenDatapoints,
  filterChildrenByHiddenDatapoints,
} from "../utils/filterHiddenDatapoints";
import { resolveHidden } from "../utils/visibilityState";
import { useAnnoFilterConfig, canDrill } from "../utils/annoDrilldown/useAnnoDrilldown";
import { AnnoDrilldownPanel, AnnoDrilldownCount } from "../utils/annoDrilldown/AnnoDrilldownPanel";

/**
 * Check if the data has sortOrder field (from backend NumberInput dimensions)
 */
const hasSortOrder = (data) => {
  if (!data?.length) return false;
  return data.some((d) => typeof d.sortOrder === 'number');
};

/**
 * Sort data by sortOrder field from backend
 * This ensures bars appear in logical order: < 0, 0-50, 50-100, 100-150, >= 150
 * instead of being sorted by count
 * Works for both custom ranges and auto-generated ranges for NumberInput dimensions
 */
const sortBySortOrder = (data) => {
  return [...data].sort((a, b) => {
    const aOrder = a.sortOrder ?? Infinity;
    const bOrder = b.sortOrder ?? Infinity;
    return aOrder - bOrder;
  });
};

const isBreakdownChart = (data) =>
  data.some((d) => d.children && d.children.length > 0);

const getMaxChildrenLength = (data) =>
  data.reduce((max, d) => Math.max(max, d.children?.length || 0), 0);

const hasNoChildren = (data) => {
  if (!data?.length) return true;
  return data.every((d) => !d.children || d.children.length === 0);
};

// Calculate the max count from original (unfiltered) data
// This ensures bar widths stay constant when bars are hidden
const calcOriginalMaxCount = (data, isBreakdown, breakdownDisplayType) => {
  if (!data?.length) return 0;
  if (!isBreakdown) {
    // Simple bar chart: max of all bar counts
    return Math.max(...data.map((d) => d.count || 0));
  }

  if (breakdownDisplayType === "stacked") {
    // Stacked breakdown chart: max of sum of children per group
    // (all segments stack into one bar, so total width = sum of all segments)
    return Math.max(
      ...data.map((d) => {
        if (!d.children?.length) return 0;
        return d.children.reduce((sum, child) => sum + (child.count || 0), 0);
      })
    );
  }

  // Grouped breakdown chart: max of individual child counts
  // (each child is a separate bar, so max width = highest individual count)
  return Math.max(
    ...data.map((d) => {
      if (!d.children?.length) return 0;
      return Math.max(...d.children.map((child) => child.count || 0));
    })
  );
};

const buildValuePath = (data) => {
  if (hasNoChildren(data)) return "count";

  const maxLen = getMaxChildrenLength(data);
  return makeArrayFromLength(maxLen).map((_, i) => `children.${i}.count`);
};

const buildTooltipLabelsPath = (data) => {
  if (hasNoChildren(data)) return undefined;

  const maxLen = getMaxChildrenLength(data);
  return makeArrayFromLength(maxLen).map((_, i) => `children.${i}.display`);
};

/**
 * cases to test for:
 * x: tag y: tag : WORKS
 * x: main. y: tag : WORKS
 * x: main. y: .main : WORKS
 * x: tag y: rolluptag : WORKS
 * x: tag y: rollupValuepath : WORKS
 *
 * x: annotag y: rollupAnnoTag : WORKS
 * x: annotag y: tag : WORKS
 * x: annotag y: main. : WORKS
 * x: rollupAnnoTag y: rollupTag
 */

export const Component = ({
  contentType,
  dimensions,
  // The dimensions as the picker stored them, `display` and `filterKey`
  // included. The processed `dimensions` above are request-shaped and drop both.
  dimension1,
  dimension2,
  id,
  widgetIdx = 0,
  activeFilters = [],
  actions,
  presetActiveFilters = [],
  widgetFilters = [],
  style = {},
  breakdownDisplayType,
  data: _data,
  // public-share snapshot replay; falls back to the legacy `data` prop (DataPivot, AiChat)
  prefetchedData = _data,
  onClick,
  summarizeAfterIdx,
  dim1Visibility,
  dim2Visibility,
  preserveDataOrder = false,
  enableVisibilityFetching = false,
  isDim1NumberInput = false,
  displayMode = "countAndPercentage",
  combineSegments = false,
  numberOfSegments,
  sortBars,
  sortGroups,
  groupsFollowFirst,
  hideZeroValues = false,
}) => {
  const allTpls = useGetQueryData(["allTpls"]);
  const { updateWidgetDataStore } = useReportLandingContext() || {};

  const breakdownCategoryName = useMemo(() => {
    if (!dimensions || dimensions.length < 2 || !contentType) return undefined;
    // What the picker showed is what the legend should say. The path-derived
    // fallback below only works for template fields; an annotation-tag
    // dimension has no template block, so it used to fall through to the raw
    // path ("Annotations.tags.tags").
    if (dimension2?.display) return dimension2.display;
    const dim = dimensions[1];
    let valuePath = typeof dim === 'string' ? dim : dim.valuePath || dim.path;
    if (Array.isArray(valuePath)) valuePath = valuePath[valuePath.length - 1];
    if (!valuePath || typeof valuePath !== 'string') return undefined;
    const tpl = allTpls?.find(t => t.kp_content_type === contentType);
    if (!tpl) return formatCamelCaseToSentenceCase(valuePath.replace("main.", ""));
    const blocks = extractAllBlocksFromTpl({ tpl });
    const block = blocks.find(b => b.valuePath === valuePath);
    return block?.props?.shortLabel || block?.props?.label ||
      formatCamelCaseToSentenceCase(valuePath.replace("main.", ""));
  }, [dimension2, dimensions, contentType, allTpls]);

  const combinedFilters = useMemo(() => {
    return [...activeFilters, ...presetActiveFilters, ...widgetFilters];
  }, [activeFilters, presetActiveFilters, widgetFilters]);

  // Only an annotation chart can answer "which annotations are in this bar",
  // and every dimension it pins has to group by a filter the config has.
  const dims = dimension2 ? [dimension1, dimension2] : [dimension1];
  const filterConfig = useAnnoFilterConfig();
  // Never on a public snapshot: the chart replays frozen data there, and the
  // panel would fire live annotation queries for an anonymous viewer.
  const barsAreDrillable = !prefetchedData && contentType === "annotations" && canDrill(dims, filterConfig);

  const drilldownProps = (datum) => ({
    datum,
    dims,
    baseFilters: combinedFilters,
    filterConfig,
  });

  const shouldFetch = prefetchedData ? false : true;

  const {
    data: dataFromApiCall,
    status: statusFromApiCall,
    error,
    ref,
  } = useAggrMetricsAcrossDimensions({
    vizCardId: id,
    contentType: contentType,
    dimensions: dimensions,
    activeFilters: combinedFilters,
    metrics: [{ type: "count", name: "count" }],
    queryOptions: { enabled: shouldFetch },
    enableVisibilityFetching,
    includeZeroCounts: !hideZeroValues,
  });

  const data = prefetchedData || dataFromApiCall;
  const status = prefetchedData ? "success" : statusFromApiCall;

  useEffect(() => {
    if (status === "success" && data?.length) {
      updateWidgetDataStore && updateWidgetDataStore(id, data);
    }
  }, [data, status, id, updateWidgetDataStore]);

  useUpdateLoadedWidgetsTracker({ status, widgetIdx, hasData: !!data?.length, widgetId: id });

  const genViz = () => {
    const isBreakdown = isBreakdownChart(data);

    // Sort by backend's sortOrder ONLY when preserveDataOrder is true
    // and backend provides sortOrder field
    // Otherwise, keep the default count-based sorting
    const sortedData = !isBreakdown && preserveDataOrder && hasSortOrder(data)
      ? sortBySortOrder(data)
      : data;

    // Calculate max from original data before filtering
    // This ensures bar widths stay constant when bars are hidden
    const originalMaxCount = calcOriginalMaxCount(sortedData, isBreakdown, breakdownDisplayType);

    // What to hide right now: the manual selection, or the Top/Bottom-N set
    // computed from the CURRENT data. Derived every render and never persisted
    // for this widget, so a dynamic selection cannot go stale against new data.
    const effectiveHiddenDim1 = resolveHidden({ state: dim1Visibility, data, dimension: 1 });
    const effectiveHiddenDim2 = resolveHidden({ state: dim2Visibility, data, dimension: 2 });

    // Percentages are the BE's, at every level (see _aggrMetricsAcrossDimensions):
    // a bar is its share of the documents in scope, a breakdown child its share
    // of its parent's document-unit count. Nothing is recomputed here, and
    // hiding never changes them: a percentage means one fixed thing and must not
    // move according to what happens to be on screen. (Hiding two of four
    // options and presenting the remaining two as 100% between them says
    // something that simply isn't true.) The old FE recompute divided a child
    // by the SUM of its siblings — the number of picks, not documents — which
    // for a multi-value breakdown answered a different, misleading question.
    let filteredData = filterByHiddenDatapoints(
      sortedData,
      effectiveHiddenDim1
    );
    filteredData = filterChildrenByHiddenDatapoints(
      filteredData,
      effectiveHiddenDim2,
      { keepEmptyGroups: !hideZeroValues }
    );

    // Preserve order when preserveDataOrder is true and backend provides sortOrder field
    const shouldPreserveOrder = !isBreakdown && preserveDataOrder && hasSortOrder(data);

    // "Others" is the user's call, not the layout's: the Display section's
    // "Combine bars" switch decides whether overflow collapses, and its stepper
    // decides how many real bars come first. (This used to be derived from the
    // widget's pixel height, so the same data summarised differently depending
    // on how tall the card happened to be — and re-summarised on resize.)
    // Infinity means "show everything, summarise nothing". Callers that render
    // this Component directly (DataPivot, AiChat) still pass their own explicit
    // summarizeAfterIdx, which wins.
    const effectiveSummarizeAfterIdx = summarizeAfterIdx ?? (
      !isBreakdown && combineSegments
        ? Math.max(
            MIN_BARS,
            numberOfSegments ?? Math.min(DEFAULT_MAX_BARS, filteredData.length)
          ) - 1
        : Infinity
    );

    return (
      <BarChart
        breakdownCategoryName={breakdownCategoryName}
        data={filteredData}
        valuePath={buildValuePath(filteredData)}
        tooltipLabelsPath={buildTooltipLabelsPath(filteredData)}
        labelPath="display"
        colorIdx={widgetIdx % 4}
        style={style}
        breakdownDisplayType={breakdownDisplayType}
        onClick={onClick}
        renderDatumDetails={
          barsAreDrillable ? (d) => <AnnoDrilldownPanel {...drilldownProps(d)} /> : undefined
        }
        renderDatumSubHeader={
          barsAreDrillable ? (d) => <AnnoDrilldownCount {...drilldownProps(d)} /> : undefined
        }
        summarizeAfterIdx={effectiveSummarizeAfterIdx}
        maxTotalCount={originalMaxCount}
        preserveOrder={shouldPreserveOrder}
        isDim1NumberInput={isDim1NumberInput}
        sortBars={sortBars}
        sortGroups={sortGroups}
        groupsFollowFirst={groupsFollowFirst}
        showCount={displayMode === "countOnly" || displayMode === "countAndPercentage"}
        showPercent={displayMode === "percentageOnly" || displayMode === "countAndPercentage"}
      />
    );
  };

  return (
    <div ref={ref}>
      {status === "loading" ? (
        <WidgetLoadingState />
      ) : status === "error" ? (
        <WidgetErrorState error={error} />
      ) : (
        status === "success" && genViz()
      )}
    </div>
  );
};

export const optionDef = {
  display: "Bar Chart",
  icon: "ChartBarHorizontal",
  value: "barChart",
  comp: "WidgetBarChart",
};

export const defaultConfig = { props: { breakdownDisplayType: "grouped" } }; //undefined; // could be some default props in this format: { props: {... } }

export const processRawConfigFn = WIDGET_ELEM_processRawConfigFn;

export const ConfigForm = (props) => (
  <BarChartConfigForm
    {...props}
    widgetIcon={optionDef.icon}
    widgetTitle={optionDef.display}
  />
);

export const someReqPropsUnset = WIDGET_ELEM_someReqPropsUnset;
